import { z } from "zod";
import { supabase } from "@/ajax/clients/supabase";
import {
  IMPERSONATE_USER_HEADER,
  ImpersonationExperience,
} from "@api/shared-runtime/impersonation";

// Session storage scopes an impersonation to one tab: other tabs stay signed
// in as the staff member, and closing the tab ends it.
export const IMPERSONATION_STORAGE_KEY = "impersonation";
const STOP_IMPERSONATION_PATH = "/admin/users";

const EXPERIENCE_HOME_PATHS: Record<ImpersonationExperience, string> = {
  [ImpersonationExperience.Organization]: "/org/home",
  [ImpersonationExperience.Provider]: "/provider/home",
};

const impersonationSessionSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
});

export type ImpersonationSession = z.infer<typeof impersonationSessionSchema>;

export function getImpersonationSession(): ImpersonationSession | null {
  try {
    const stored = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = impersonationSessionSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function isImpersonating(): boolean {
  return getImpersonationSession() !== null;
}

/** Headers that make an API request run as the impersonated user. */
export function getImpersonationHeaders(): Record<string, string> {
  const session = getImpersonationSession();
  if (!session) {
    return {};
  }
  return { [IMPERSONATE_USER_HEADER]: session.userId };
}

function clearImpersonationSession() {
  try {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    // Storage unavailable: there is no session to clear.
  }
}

/**
 * Starts viewing the app as another user. A full page load discards every
 * cached query and route loader result from the staff member's own session.
 */
export function startImpersonation({
  session,
  experience,
}: {
  session: ImpersonationSession;
  experience: ImpersonationExperience;
}) {
  sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
  window.location.assign(EXPERIENCE_HOME_PATHS[experience]);
}

export function stopImpersonation() {
  clearImpersonationSession();
  window.location.assign(STOP_IMPERSONATION_PATH);
}

/**
 * Signing out ends the impersonation too, so the next sign-in in this tab
 * starts as the person signing in. Call once at app startup.
 */
export function clearImpersonationOnSignOut() {
  supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_OUT") {
      clearImpersonationSession();
    }
  });
}
